import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Copy, Book, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const ApiDocumentation = () => {
  const [expandedResponses, setExpandedResponses] = useState({});

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const toggleResponse = (key) => {
    setExpandedResponses(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const endpoints = {
    campaign: [
      {
        method: 'GET',
        endpoint: '/api/campaign/all',
        description: 'Get all campaigns associated with your account.',
        parameters: [],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/campaign/all" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `[
  {
    "campaign_name": "Sample_Campaign_One",
    "id": "111111111111111111.1111"
  },
  {
    "campaign_name": "Sample_Campaign_Two",
    "id": "222222222222222222.2222"
  }
]`
      },
      {
        method: 'GET',
        endpoint: '/api/campaign/{campaign_id}',
        description: 'Get details of a specific campaign.',
        parameters: [
          { name: 'campaign_id', required: true, description: 'The ID of the campaign for which you want to get details.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/campaign/123" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "campaign_name": "Dummy_Campaign_Name",
  "type": "Dummy_Campaign_Type",
  "start_date": "1/1/2026",
  "end_date": "3/31/2026",
  "total_target": "100",
  "total_budget": "999.99",
  "delivery_days": ["Monday", "Wednesday", "Friday"],
  "status": "Inactive",
  "specifications": {
    "job_title_keywords": "Sample Title One, Sample Title Two, Sample Title Three",
    "employee_size": ["1-10", "11-50", "51-200"],
    "industry_types": ["Sample Industry A", "Sample Industry B", "Sample Industry C"],
    "revenue_size": ["Below 1M", "1M - 10M", "10M - 50M"],
    "geo": ["Country A", "Country B"],
    "job_functions": ["Function One", "Function Two"],
    "job_levels": ["Level One", "Level Two"],
    "suppression_list": true,
    "suppression_listname": "Dummy_Suppression_List",
    "call_summary": true,
    "account_list": true,
    "account_listname": "Dummy_Account_List",
    "contacts_cap": 5,
    "segmentation": false,
    "segments": ["SEG_A", "SEG_B"],
    "global_deduplication_window": "3 months",
    "lead_types": ["TYPE_A", "TYPE_B"]
  },
  "assets": [
    {
      "asset_name": "Dummy Asset Name",
      "asset_type": "Dummy Asset Type",
      "remarks": "This is a dummy remark for the asset."
    }
  ],
  "custom_questions": [
    {
      "question": "Dummy question one?",
      "answers": ["Option A", "Option B", "Option C"]
    },
    {
      "question": "Dummy question two?",
      "answers": ["Choice 1", "Choice 2"]
    }
  ],
  "geo_wise_targets": null,
  "month_wise_pacing": [
    { "month": "2026-January", "pacing": 50 },
    { "month": "2026-February", "pacing": 50 }
  ],
  "client_pricing": [
    { "lead_type": "TYPE_A", "price": "99.00", "target": 40 },
    { "lead_type": "TYPE_B", "price": "49.00", "target": 60 }
  ],
  "remarks": "This is a completely dummy remarks field used only for testing and documentation purposes."
}`
      }
    ],
    leads: [
      {
        method: 'GET',
        endpoint: '/api/leads/{campaign_id}/all',
        description: 'Get all leads for a specific campaign.',
        parameters: [
          { name: 'campaign_id', required: true, description: 'The ID of the campaign for which you want to get leads.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/leads/123/all" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `[
  {
    "id": 1001,
    "campaign": "Dummy_Campaign_A",
    "name": "Mr. John Doe",
    "title": "Sample Job Title One",
    "address": "123 Sample Street, City A, State A, 00001, Country A",
    "web_url": "https://example.com/profile/johndoe",
    "lead_type": "TYPE_A",
    "status": "Accepted",
    "reason": null,
    "comments": null
  },
  {
    "id": 1002,
    "campaign": "Dummy_Campaign_A",
    "name": "Ms. Jane Smith",
    "title": "Sample Job Title Two",
    "address": "456 Demo Avenue, City B, State B, 00002, Country A",
    "web_url": "https://example.com/profile/janesmith",
    "lead_type": "TYPE_B",
    "status": "Accepted",
    "reason": null,
    "comments": null
  },
  {
    "id": 1003,
    "campaign": "Dummy_Campaign_A",
    "name": "Mr. Alex Johnson",
    "title": "Sample Job Title Three",
    "address": "789 Test Road, City C, State C, 00003, Country A",
    "web_url": "https://example.com/profile/alexjohnson",
    "lead_type": "TYPE_B",
    "status": "Accepted",
    "reason": null,
    "comments": null
  },
  {
    "id": 1004,
    "campaign": "Dummy_Campaign_A",
    "name": "Mr. Robert Brown",
    "title": "Sample Job Title Four",
    "address": "101 Dummy Blvd, City D, State D, 00004, Country A",
    "web_url": "https://example.com/profile/robertbrown",
    "lead_type": "TYPE_A",
    "status": "Accepted",
    "reason": null,
    "comments": null
  },
  {
    "id": 1005,
    "campaign": "Dummy_Campaign_A",
    "name": "Ms. Emily Davis",
    "title": "Sample Job Title Five",
    "address": "202 Placeholder Lane, City E, State E, 00005, Country A",
    "web_url": "https://example.com/profile/emilydavis",
    "lead_type": "TYPE_B",
    "status": "Accepted",
    "reason": null,
    "comments": null
  }
]`
      },
      {
        method: 'GET',
        endpoint: '/api/leads/{lead_id}',
        description: 'Get details of a specific lead.',
        parameters: [
          { name: 'lead_id', required: true, description: 'The ID of the lead you want to retrieve.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/leads/123" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "id": "999",
  "campaign": "Dummy_Campaign_A",
  "created_at": "1760000000",
  "basic_details": {
    "salutation": "Mr.",
    "first_name": "John",
    "last_name": "Doe"
  },
  "employment_details": {
    "job_title": "Sample Job Title",
    "company_name": "Dummy Company Ltd",
    "industry_type": "Sample Industry",
    "employee_size": "51-200 employees",
    "revenue_size": "10 - 20 Million",
    "job_function": "Operations",
    "job_level": "Senior"
  },
  "contact_details": {
    "email": "john.doe@example.com",
    "contact_number": "00-1234567890",
    "direct_number": "00-0987654321",
    "address1": "123 Dummy Street",
    "address2": "Suite 100",
    "city": "Sample City",
    "state": "ST",
    "zip_code": "00000",
    "country": "Country A",
    "web_url": "https://example.com/profile/johndoe"
  },
  "interactions": [
    {
      "question": "Sample question one?",
      "answer": "Sample answer A"
    },
    {
      "question": "Sample question two?",
      "answer": "Sample answer B"
    }
  ],
  "other_details": {
    "lead_type": "TYPE_A",
    "segment": "SEG_A",
    "asset_name": "Dummy Asset Name",
    "additional_comments": "This is a dummy lead used for testing."
  },
  "audit_details": {
    "audit_date": "2026-01-15 10:30:00",
    "status": "Accepted",
    "reason": null,
    "comments": null
  }
}`
      }
    ],
    partner: [
      {
        method: 'GET',
        endpoint: '/api/partner/all',
        description: 'Get all partners associated with your account.',
        parameters: [],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/partner/all" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `[
  {
    "id": "aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa",
    "company_name": "Dummy Company Pvt. Ltd.",
    "first_name": "Test",
    "last_name": "User",
    "email": "test.user@example.com",
    "status": "Active"
  }
]`
      },
      {
        method: 'GET',
        endpoint: '/api/partner/{partner_id}',
        description: 'Get details of a specific partner.',
        parameters: [
          { name: 'partner_id', required: true, description: 'The ID of the partner for which you want to get details.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/partner/123" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "id": "bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb",
  "status": "Active",
  "personal_info": {
    "first_name": "Demo",
    "last_name": "User",
    "email": "demo.user@example.com",
    "phone": "9999999999"
  },
  "professional_info": {
    "job_title": "Sample Job Title",
    "company_name": "Dummy Organization Ltd."
  },
  "location_info": {
    "address": "123 Example Business Park",
    "city": "Sample City",
    "state": "Sample State",
    "zip_code": "000000",
    "country": "Country A"
  },
  "tax_info": {
    "gstin": null
  }
}`
      },
      {
        method: 'GET',
        endpoint: '/api/partner/{partner_id}/performance',
        description: 'Get performance metrics for a specific partner.',
        parameters: [
          { name: 'partner_id', required: true, description: 'The ID of the partner for which you want to get performance data.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/partner/123/performance" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "id": "cccccccccccccccccccccccccccccccc",
  "company_name": "Dummy Company Pvt. Ltd.",
  "campaigns": 3,
  "allocation": 100,
  "leads_submitted": 50,
  "leads_accepted": 40,
  "leads_rejected": 5,
  "leads_in_review": 5,
  "percent_quality": 80.00,
  "performance": [
    {
      "id": "111111111111111111.1111",
      "name": "Dummy Campaign One",
      "allocation": 60,
      "leads_submitted": 30,
      "leads_accepted": 28,
      "leads_rejected": 1,
      "leads_in_review": 1,
      "percent_quality": 93.33,
      "percent_progress": 70.00
    },
    {
      "id": "222222222222222222.2222",
      "name": "Dummy Campaign Two",
      "allocation": 40,
      "leads_submitted": 20,
      "leads_accepted": 12,
      "leads_rejected": 4,
      "leads_in_review": 4,
      "percent_quality": 60.00,
      "percent_progress": 30.00
    }
  ]
}`
      },
      {
        method: 'GET',
        endpoint: '/api/partner/{partner_id}/billing',
        description: 'Get billing information for a specific partner.',
        parameters: [
          { name: 'partner_id', required: true, description: 'The ID of the partner for which you want to get billing details.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/partner/123/billing" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "id": "dddddddddddddddddddddddddddddddd",
  "company_name": "Dummy Company Pvt. Ltd.",
  "total_billing": "999.00 USD",
  "billings": [
    { "month": "2026-January", "billing": "500.00 USD" },
    { "month": "2026-February", "billing": "499.00 USD" }
  ]
}`
      }
    ],
    account: [
      {
        method: 'GET',
        endpoint: '/api/account/{account_id}',
        description: 'Get account details.',
        parameters: [
          { name: 'account_id', required: true, description: 'The ID of your account.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/account/123" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "id": "eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
  "accountholder": "Demo User",
  "job_title": "Sample Job Role",
  "company_name": "Dummy Digital Solutions Pvt. Ltd.",
  "email_address": "demo.user@example.com",
  "phone": "910000000000",
  "address": "Sample Area, Sample City, Sample State 000000, Country A",
  "tax_id": "N/A",
  "wallet_balance": 150.00,
  "subscription_details": {
    "plan": "basic",
    "renewal_date": 1770000000
  },
  "addon_details": [
    {
      "addon_name": "Sample Addon One",
      "purchased_on": 1760000000,
      "purchase_qty": 2,
      "available_qty": 1,
      "status": "used"
    },
    {
      "addon_name": "Sample Addon Two",
      "purchased_on": 1761000000,
      "purchase_qty": 1,
      "available_qty": 1,
      "status": "unused"
    }
  ],
  "usage": {
    "campaign": "1 of 5 campaigns used",
    "leads": "100 of 1000 leads used"
  },
  "status": "active",
  "created_on": 1758000000
}`
      },
      {
        method: 'GET',
        endpoint: '/api/transactions/{account_id}',
        description: 'Get transaction history for an account.',
        parameters: [
          { name: 'account_id', required: true, description: 'The ID of your account.' }
        ],
        example: 'curl -X GET "https://portal.aalign.io/v1/api/transactions/123" \\\n  -H "api_key: your_api_key_here"',
        sampleResponse: `{
  "payments": [
    {
      "id": "PAYMENT_ABC123",
      "item": "Basic Plan @50 USD",
      "units": 1,
      "amount": "59.00 USD",
      "created_at": 1770001000
    },
    {
      "id": "PAYMENT_DEF456",
      "item": "Wallet Recharge",
      "units": 5,
      "amount": "25.00 USD",
      "created_at": 1770002000
    },
    {
      "id": "PAYMENT_GHI789",
      "item": "Wallet Recharge",
      "units": 20,
      "amount": "100.00 USD",
      "created_at": 1770003000
    }
  ],
  "wallet_transactions": [
    {
      "txn_id": "TXN_100001",
      "transaction_date": 1770000000,
      "type": "credit",
      "item": "Wallet Recharge",
      "credits": 5,
      "balance_after": 5.00
    },
    {
      "txn_id": "TXN_100002",
      "transaction_date": 1770000500,
      "type": "debit",
      "item": "Sample Addon",
      "credits": -5,
      "balance_after": 0.00
    },
    {
      "txn_id": "TXN_100003",
      "transaction_date": 1770001000,
      "type": "credit",
      "item": "Wallet Recharge",
      "credits": 20,
      "balance_after": 20.00
    }
  ]
}`
      }
    ]
  };

  const renderEndpoint = (endpoint, idx, category) => {
    const responseKey = `${category}-${idx}`;
    const isExpanded = expandedResponses[responseKey];

    return (
      <div key={idx} className="border-l-2 border-gray-200 pl-6 mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded font-mono text-sm font-semibold">
            {endpoint.method}
          </span>
          <code className="text-base font-mono text-gray-800 bg-gray-100 px-3 py-1 rounded">
            {endpoint.endpoint}
          </code>
        </div>
        <p className="text-gray-700 mb-4">{endpoint.description}</p>
        
        {endpoint.parameters.length > 0 && (
          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-2">Parameters</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Name</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.parameters.map((param, paramIdx) => (
                    <tr key={paramIdx} className="border-b">
                      <td className="px-4 py-3">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                          {param.name}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-red-600 text-sm font-medium">
                          {param.required ? 'required' : 'optional'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mb-4">
          <h4 className="font-semibold text-gray-900 mb-2">Example Request</h4>
          <div className="relative bg-gray-900 text-gray-100 p-4 rounded-lg">
            <button
              onClick={() => copyToClipboard(endpoint.example)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
            >
              <Copy className="w-4 h-4" />
            </button>
            <pre className="text-sm font-mono overflow-x-auto">
              <code>{endpoint.example}</code>
            </pre>
          </div>
        </div>

        {endpoint.sampleResponse && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Sample Response</h4>
              <button
                onClick={() => toggleResponse(responseKey)}
                className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide Response
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show Response
                  </>
                )}
              </button>
            </div>
            {isExpanded && (
              <div className="relative bg-gray-900 text-gray-100 p-4 rounded-lg">
                <button
                  onClick={() => copyToClipboard(endpoint.sampleResponse)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <pre className="text-sm font-mono overflow-x-auto">
                  <code>{endpoint.sampleResponse}</code>
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 scroll-smooth">
      <style>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-white border-b">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Book className="w-4 h-4" />
            Documentation
          </div>
          <h1 className="text-4xl lg:text-5xl font-black mb-4">
            API Reference
          </h1>
          <p className="text-lg text-gray-600">
            Integrate Aalign into your applications with our RESTful API. All endpoints require authentication via API key.
          </p>
        </div>
      </section>

      {/* Navigation Bar */}
      <nav className="sticky top-16 z-40 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3">
            <a
              href="#base-url"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Base URL
            </a>
            <a
              href="#authentication"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Authentication
            </a>
            <a
              href="#campaign-api"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Campaign API
            </a>
            <a
              href="#leads-api"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Leads API
            </a>
            <a
              href="#partner-api"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Partner API
            </a>
            <a
              href="#account-api"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg whitespace-nowrap transition-colors"
            >
              Account API
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-16">
          
          {/* Base URL */}
          <div id="base-url">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Base URL</h2>
            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm">
              https://portal.aalign.io/v1
            </div>
          </div>

          {/* Authentication */}
          <div id="authentication">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Authentication</h2>
            <p className="text-gray-600 mb-4">
              All API requests require authentication via an API key passed in the header.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded font-mono text-sm font-semibold">
                GET
              </span>
              <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono">
                Header: api_key
              </code>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r text-sm text-gray-700">
              Go to <strong>Profile</strong> section and generate your API Key. This API key must be passed as a header <code className="bg-blue-100 px-2 py-1 rounded">api_key</code> to access all our APIs.
            </div>
          </div>

          {/* Campaign API */}
          <div id="campaign-api">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Campaign API</h2>
            <p className="text-gray-600 mb-6">Manage and retrieve campaign information.</p>
            <div>
              {endpoints.campaign.map((endpoint, idx) => renderEndpoint(endpoint, idx, 'campaign'))}
            </div>
          </div>

          {/* Leads API */}
          <div id="leads-api">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Leads API</h2>
            <p className="text-gray-600 mb-6">Access and manage lead data.</p>
            <div>
              {endpoints.leads.map((endpoint, idx) => renderEndpoint(endpoint, idx, 'leads'))}
            </div>
          </div>

          {/* Partner API */}
          <div id="partner-api">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Partner API</h2>
            <p className="text-gray-600 mb-6">Manage partner relationships and data.</p>
            <div>
              {endpoints.partner.map((endpoint, idx) => renderEndpoint(endpoint, idx, 'partner'))}
            </div>
          </div>

          {/* Account API */}
          <div id="account-api">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Account API</h2>
            <p className="text-gray-600 mb-6">Access account and transaction information.</p>
            <div>
              {endpoints.account.map((endpoint, idx) => renderEndpoint(endpoint, idx, 'account'))}
            </div>
          </div>

          {/* Need Help Section */}
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Need Help?</h3>
            <p className="text-orange-50 mb-6">
              Our team is here to help you integrate with the Aalign API.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-orange-600 hover:bg-gray-100 font-semibold px-6 py-3">
                Contact Support
              </Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-6 py-3">
                View Examples
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApiDocumentation;
